/**
 * Replay-time filter for the WebSocket pending queue.
 *
 * Frames captured during disconnect can become stale by the time the socket
 * comes back: server may have advanced the phase, eliminated the target, or
 * dropped the player. Replaying those frames triggers server-side `Error`
 * responses (`wrong_phase`, `invalid_target`, `not_a_member`) which pollute
 * the engine_error analytics signal.
 *
 * This filter is kind-aware but does NOT reproduce engine rules — only the
 * coarse-grain checks that catch the common races. Anything we miss falls
 * through to the server (correct behaviour, just noisier).
 */
import { type ClientMsg, ClientMsgKind, Phase } from '@infiltre/shared';

export interface ReplayFilterSnapshot {
  phase: Phase;
  players: ReadonlyArray<{ id: string }>;
}

export type FrameFilter = (frame: ClientMsg) => boolean;

/** kind → phases in which the action is still valid. */
const ALLOWED_PHASES: Partial<Record<ClientMsgKind, ReadonlyArray<Phase>>> = {
  [ClientMsgKind.JoinRoom]: [Phase.Lobby],
  [ClientMsgKind.StartGame]: [Phase.Lobby],
  [ClientMsgKind.UpdateRoomOptions]: [Phase.Lobby],
  [ClientMsgKind.OpenVote]: [Phase.Discuss],
  [ClientMsgKind.CastVote]: [Phase.Vote],
  [ClientMsgKind.ClochardGuess]: [Phase.ClochardGuess],
  [ClientMsgKind.EliminatePlayer]: [Phase.Discuss, Phase.Vote],
  [ClientMsgKind.NextRound]: [Phase.RevealVote, Phase.Finished],
  [ClientMsgKind.RequestNextRound]: [Phase.RevealVote, Phase.Finished],
};

const isInPlayers = (players: ReplayFilterSnapshot['players'], id: string): boolean =>
  players.some((p) => p.id === id);

export const makeReplayFilter = (getSnapshot: () => ReplayFilterSnapshot | null): FrameFilter => {
  return (frame: ClientMsg): boolean => {
    const snap = getSnapshot();
    if (!snap) return true; // no snapshot yet — let server arbitrate

    const allowed = ALLOWED_PHASES[frame.kind];
    if (allowed !== undefined && !allowed.includes(snap.phase)) {
      return false;
    }

    if (
      (frame.kind === ClientMsgKind.CastVote || frame.kind === ClientMsgKind.EliminatePlayer) &&
      !isInPlayers(snap.players, frame.targetPlayerId)
    ) {
      return false;
    }
    if (frame.kind === ClientMsgKind.PromoteHost && !isInPlayers(snap.players, frame.newHostId)) {
      return false;
    }

    return true;
  };
};
